'use client';

import type { PublicUserDocument } from '@models/user';
import ProductPriceRange from '@ui/ProductPriceRange';
import { getItem, logEvent } from '@util/analytics';
import {
  trackUserInteraction,
  trackUserSearch,
} from '@util/firestore/recommendations';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import { cn } from '@util/index';
import { getProductSlug } from '@util/urlHelpers';
import { useAttributionContext } from 'context/AttributionContext';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import type { OrderItemDocument } from 'models/order';
import { isVehicleCategory, type ProductLight } from 'models/product';
import { MotionDiv } from 'motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FavoriteButton } from './FavoriteButton';
import ProductFitment from './ProductFitment';
import { ProductPicture } from './ProductPicture';
import ProductShippingEstimate from './ProductShippingEstimate';
import { ProductTitle } from './ProductTitle';
import { HTMLAttributeAnchorTarget } from 'react';

const MXProductCard = ({
  product,
  list_id,
  list_name,
  attribution,
  bgColor,
  priority,
  whiteText,
  noLink,
  isSelected,
  onSelect,
  linkTarget,
}: {
  product: ProductLight;
  seller?: PublicUserDocument;
  list_id?: string;
  list_name?: string;
  attribution: OrderItemDocument['attribution'];
  bgColor?: string;
  priority?: boolean;
  whiteText?: boolean;
  noLink?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  linkTarget?: HTMLAttributeAnchorTarget;
}) => {
  // const { increaseCartQty, canAddProductToCart, setCartOpen } =
  //   useShoppingCart();
  const { setAttribution, getAttribution } = useAttributionContext();
  const pathname = usePathname();
  const { userDoc, observeProduct } = useAuth();
  const { filters } = useSearch();
  // const router = useRouter();

  // const { data: sellerDoc } = useQuery({
  //   queryKey: ['seller_doc', product.seller_id],
  //   queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
  //   enabled: Boolean(product.seller_id),
  // });

  // const handleAddToCart = () => {
  //   if (userDoc) {
  //     if (canAddProductToCart(product.id)) {
  //       const attribution = getAttribution(product.id);
  //       const cartItem = getCartItemFromProductDocument(
  //         product,
  //         null,
  //         attribution
  //       );

  //       increaseCartQty(cartItem);
  //       setCartOpen(true);
  //     } else {
  //       setCartOpen(true);
  //     }
  //   } else {
  //     router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
  //   }
  // };

  const ref = useLogWhenInView(() => {
    if (userDoc?.uid) {
      const attributionObj = ['gear_for_you', 'parts_for_you'].includes(
        list_id ?? ''
      )
        ? {
            page: 'for_you',
            section: list_id ?? '',
            data_source: 'typesense',
          }
        : attribution;
      observeProduct({
        id: product.id,
        attribution: attributionObj,
      });
    }
  });

  const isFavoritesPage =
    pathname?.includes('/favorites') || attribution?.page === 'favorites';

  return (
    <MotionDiv
      ref={ref}
      className={cn(
        'group relative mx-auto flex h-fit w-full flex-col sm:mx-0 sm:w-[29rem] sm:rounded-[1.5rem]',
        isFavoritesPage
          ? 'bg-white transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-xl lg:shadow-lg'
          : bgColor || ''
      )}
    >
      <Link
        href={noLink ? '#' : getProductSlug(product)}
        target={linkTarget}
        className="flex h-full flex-col"
        onClick={() => {
          setAttribution(product.id, attribution);
          logEvent('select_item', {
            items: [getItem(product)],
            item_list_id: list_id ?? pathname,
            item_list_name: list_name ?? pathname,
          });
          if (pathname?.includes('search') || pathname?.includes('product')) {
            if (userDoc)
              trackUserInteraction({
                uid: userDoc.uid,
                pid: product.id,
                interaction: 'search_clicked',
                attribution,
              });
            if (filters?.term) {
              trackUserSearch({
                ...filters,
                product_clicked: product.id,
              });
            }
          }
        }}
      >
        <div className="flex h-full flex-col">
          <ProductPicture product={product} priority={priority} />

          <div
            className={cn(
              'space-y- mt-2 flex flex-grow flex-col justify-between p-[0.8rem]',
              whiteText ? 'text-white' : ''
            )}
          >
            <div className="space-y-2">
              <ProductTitle
                product={product}
                whiteText={whiteText}
                isFavoritesPage={
                  pathname?.includes('/favorites') ||
                  attribution?.page === 'favorites'
                }
              />

              <div
                className={cn(
                  pathname?.includes('/favorites') ||
                    attribution?.page === 'favorites'
                    ? 'mb-2'
                    : ''
                )}
              >
                <ProductPriceRange
                  product={product}
                  vertical
                  textColor={whiteText ? 'white' : 'default'}
                  enableCouponDiscount={!isVehicleCategory(product.category)}
                  hideSaleBadge={
                    pathname?.includes('/favorites') ||
                    attribution?.page === 'favorites'
                  }
                />
              </div>

              {!(
                pathname?.includes('/favorites') ||
                attribution?.page === 'favorites'
              ) && (
                <ProductShippingEstimate
                  userDoc={userDoc}
                  product={product}
                  whiteText={whiteText}
                />
              )}

              {userDoc && !!userDoc.bikes.length && (
                <ProductFitment product={product} userDoc={userDoc} />
              )}

              {product.sold_count && product.sold_count >= 5 && (
                <span className="block text-[1.6rem] font-medium text-brand-secondary sm:text-[1.7rem]">
                  {product.sold_count} sold
                </span>
              )}

              {/* {!!product.stock &&
                product.stock <= 3 &&
                !product.has_variations && (
                  <span className="block text-[1.6rem] font-semibold text-brand-secondary sm:text-[1.7rem]">
                    {product.stock === 1
                      ? 'Only one left'
                      : `Only ${product.stock} left`}
                  </span>
                )} */}
            </div>
          </div>
        </div>
      </Link>

      <div className="absolute right-[1rem] top-[1rem] flex w-full justify-end sm:right-[1rem]">
        {onSelect ? (
          <button
            type="button"
            className={cn(
              'z-10 flex h-12 w-12 items-center justify-center rounded-lg border-2 transition-colors',
              isSelected
                ? 'border-blue-600 bg-blue-600'
                : 'border-gray-300 bg-white/70 backdrop-blur-lg hover:border-blue-400'
            )}
            onClick={(e) => {
              e.preventDefault();
              onSelect();
            }}
          >
            {isSelected && (
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </button>
        ) : (
          <FavoriteButton
            product={product}
            initialCount={product.favorite_count}
            attribution={attribution}
          />
        )}
      </div>

      {/* {pathname === '/my-cart' && (
        <div className="absolute right-[1rem] top-[15rem] flex w-full cursor-pointer items-end justify-end lg:right-[2rem] lg:top-[23rem]">
          <AddToCartButton handleAddToCart={handleAddToCart} />
        </div>
      )} */}
    </MotionDiv>
  );
};

export default MXProductCard;
